// Top-level error boundary so render crashes (especially in AuthGate /
// Dashboard / Firebase init) never leave the user staring at a blank page.
// Renders a friendly recovery screen + a brief technical message so we can
// debug without needing to ask the user "what was on screen?".

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Forward to console + GA so we see this in dashboards.
    console.error('[ErrorBoundary] caught render error:', error, errorInfo);
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'app_error_boundary', {
          error_message: String(error?.message || error).slice(0, 250),
          error_stack: String(error?.stack || '').slice(0, 500),
          component_stack: String(errorInfo?.componentStack || '').slice(0, 500),
          path: typeof window !== 'undefined' ? window.location.pathname : '',
        });
      }
    } catch (_) { /* never let the boundary itself throw */ }
  }

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errorMessage = String(this.state.error?.message || this.state.error || 'Unknown error');

    return (
      <div style={wrapStyle}>
        <div style={cardStyle}>
          <div style={iconStyle}>⚠️</div>
          <h1 style={titleStyle}>Something went wrong.</h1>
          <p style={subStyle}>
            The audit hit an unexpected error. Your data is safe. Reload to try again, or head home and start over.
          </p>
          <div style={buttonRowStyle}>
            <button onClick={this.handleReload} style={primaryBtnStyle}>Reload page</button>
            <button onClick={this.handleHome} style={ghostBtnStyle}>Go home</button>
          </div>
          <details style={detailsStyle}>
            <summary style={summaryStyle}>Show technical details</summary>
            <pre style={preStyle}>{errorMessage}</pre>
          </details>
          <p style={footStyle}>
            If this keeps happening, email <a href="mailto:support@growleads.io" style={linkStyle}>support@growleads.io</a> with a screenshot.
          </p>
        </div>
      </div>
    );
  }
}

const wrapStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 999999,
  background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 50%, #f5f3ff 100%)',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  boxSizing: 'border-box',
  overflowY: 'auto',
};
const cardStyle = {
  background: 'white',
  borderRadius: '24px',
  padding: '40px 32px',
  maxWidth: '480px',
  width: '100%',
  boxShadow: '0 20px 60px rgba(15,23,42,0.12)',
  textAlign: 'center',
  border: '1px solid rgba(226,232,240,0.6)',
};
const iconStyle = { fontSize: 44, marginBottom: 12 };
const titleStyle = { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 12px', lineHeight: 1.3 };
const subStyle = { fontSize: 14.5, color: '#475569', margin: '0 0 24px', lineHeight: 1.55 };
const buttonRowStyle = { display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' };
const primaryBtnStyle = {
  padding: '11px 22px', background: '#0ea5e9', color: 'white',
  border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
};
const ghostBtnStyle = {
  padding: '11px 22px', background: 'white', color: '#0f172a',
  border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
};
const detailsStyle = { textAlign: 'left', marginTop: 16 };
const summaryStyle = { cursor: 'pointer', fontSize: 12.5, color: '#64748b', userSelect: 'none' };
const preStyle = {
  marginTop: 10, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0',
  borderRadius: 8, fontSize: 11.5, color: '#475569', overflow: 'auto',
  maxHeight: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};
const footStyle = { marginTop: 18, fontSize: 12, color: '#94a3b8' };
const linkStyle = { color: '#0ea5e9', textDecoration: 'none' };

export default ErrorBoundary;
